<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="delivery">
            {{ delivery.name }}
        </template>

        <template v-slot:header v-if="delivery">
            <div class="row mb-hf">
                <div class="col">
                    <span class="text-muted">
                        {{ delivery.company.name }}
                    </span>
                </div>
            </div>
            <div class="row mb-0">
                <div class="col">
                    <span class="text-muted">
                        {{ delivery.public_id }}
                    </span>
                    <a class="ml-hf" href @click.prevent="$bus.copyToClipboard(delivery.public_id)">
                        <i class="far fa-copy text-primary" aria-hidden="true" title="Copy"></i>
                    </a>
                </div>
            </div>
            <div v-if="!delivery.is_active" class="row mb-0">
                <div class="col">
                    <span class="text-danger-dark text-bold text-italic">
                        Inactive
                    </span>
                </div>
            </div>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabDeliveryContentMain" id="tabDeliveryLinkMain" data-toggle="tab" role="tab" aria-controls="tabDeliveryContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content" v-if="delivery">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabDeliveryContentMain" role="tabpanel" aria-labelledby="tabDeliveryLinkMain">
                    <!-- Delivery Address widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Address Information</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editDeliveryAddress">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        <template v-if="delivery.address_1">{{ delivery.address_1 }} {{ delivery.address_2 }}<br/></template>
                                        <template v-if="delivery.city">{{ delivery.city }}, </template>{{ delivery.state }} {{ delivery.postal_code }}
                                    </span>
                                    <label>Address</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.care_of }}
                                    </span>
                                    <label>Care Of</label>
                                </div>
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.is_default ? 'Yes' : 'No' }}
                                    </span>
                                    <label>Use as Default Delivery Address for Company</label>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Delivery Method widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Delivery Method</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editDeliveryMethod">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.carrier_pretty }}
                                    </span>
                                    <label>Carrier</label>
                                </div>
                                <div v-if="delivery.method" class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.method_pretty }}
                                    </span>
                                    <label>Method</label>
                                </div>
                                <div v-if="delivery.carrier == 'fedex' || delivery.carrier == 'ups'" class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.use_own_account ? 'Client' : 'Paymasters' }}
                                    </span>
                                    <label>Account</label>
                                </div>
                                <div v-if="delivery.use_own_account">
                                    <div class="mui-textfield">
                                        <span class="textfield">
                                            {{ delivery.account_name }}
                                        </span>
                                        <label>Account Name</label>
                                    </div>
                                    <div class="mui-textfield">
                                        <span class="textfield">
                                            {{ delivery.account_number }}
                                        </span>
                                        <label>Account Number</label>
                                    </div>
                                </div>
                                <div v-if="delivery.instructions" class="mui-textfield">
                                    <span class="textfield">
                                        {{ delivery.instructions }}
                                    </span>
                                    <label>Instructions</label>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <info-widget :collapsible="true" :is-expanded="positionsExpanded" @toggle_expand="positionsExpanded = !positionsExpanded">
                        <template v-slot:title>Positions ({{ activePositions.length || 0 }})</template>

                        <slot>
                            <div v-if="activePositions.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Positions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="position in activePositions" :key="position.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ position.terminal_value }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This delivery address is not assigned to any positions yet.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-delivery-modal v-if="delivery" ref="editDeliveryModal" :delivery="delivery" :editMode="editMode" :client="client" @updated="onUpdate" @close="editDeliveryModalOpen = false; editMode = ''" />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import EditDeliveryModal from './EditDeliveryModal'

export default {
    props: ['deliveryId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, EditDeliveryModal},
    computed: {
        activePositions() {
            let ret = []
            if (this.delivery?.positions) {
                ret = this.delivery.positions.filter(p => p.is_active)
            }
            return ret
        },
    },
    data() {
        return {
            delivery: null,
            activeTab: this.tab || 'main',
            editDeliveryModalOpen: false,
            positionsExpanded: true,
            editMode: '',
        }
    },
    watch: {
        deliveryId() {
            this.getDelivery()
        },
    },
    mounted() {
        this.getDelivery()
    },
    methods: {
        getDelivery() {
            this.delivery = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/delivery-addresses/${this.deliveryId}`).then(resp => {
                this.delivery = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editDeliveryAddress() {
            this.editMode = 'address'
            this.editDeliveryModalOpen = true
            this.$refs.editDeliveryModal.open()
        },
        editDeliveryMethod() {
            this.editMode = 'method'
            this.editDeliveryModalOpen = true
            this.$refs.editDeliveryModal.open()
        },
        onUpdate(delivery) {
            if (delivery) {
                Object.assign(this.delivery, delivery)
            }
            this.$emit('updated')
        },
    },
}
</script>
